import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { ConfirmationDialogComponent } from "../shared/confirmation-dialog/confirmation-dialog.component";
import { ClientService } from "./client.service";
import {
  ClientWithStats,
  CLIENT_PLAN_NAMES,
  CLIENT_PLAN_OPTIONS,
  type AddClientForm,
  type ClientPlanId,
} from "./client.interface";
import { OnboardingService } from "../onboarding/onboarding.service";
import { ToastrService } from "../shared/toastr.service";
import { SuperAdminDialogComponent } from "./super-admin-dialog/super-admin-dialog.component";
import { Observable, ReplaySubject, Subject, Subscription } from "rxjs";
import { shareReplay, switchMap, takeUntil } from "rxjs/operators";
import { defaultTrialFinishDate } from "../shared/utils/trial.util";
import { validateFutureDate } from "../shared/validators/date.validator";
import { validateHTTPExists } from "../shared/validators/url.validator";
import { environment } from "../../environments/environment";
import { ReportsService } from "../shared/reports/reports.service";
import { HttpClient } from "@angular/common/http";
import { AuthService } from "../auth/auth.service";
import { AnalyticsRangeDialogComponent } from "../analytics-dashboard/analytics-range-dialog/analytics-range-dialog.component";
import { AnalyticsRangeDialogResult } from "../analytics-dashboard/analytics.interface";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../shared/error-state-matcher";

@Component({
  selector: "app-client",
  templateUrl: "./client.component.html",
  styleUrls: ["./client.component.scss"],
  standalone: false,
})
export class ClientComponent implements OnInit, OnDestroy {
  public removing = false;
  public removingDomain: string | null = null;
  public saving = false;
  public billing = false;
  isDownloadingAnalytics = false;
  isDevelopmentEnvironment = !environment.production;
  readonly planOptions = CLIENT_PLAN_OPTIONS;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  private readonly trialFinishDateValidations = [
    (c: FormControl) => Validators.required(c),
    validateFutureDate,
  ];
  private readonly destroy$ = new Subject<void>();
  private currentClients: ClientWithStats[] = [];
  private clientsSubscription: Subscription;

  public spreadsheetUrl = environment.production
    ? "https://docs.google.com/spreadsheets/d/1s7bO01RMSrBvSomjtn-Rl_qPGtzzr3DJcxC0DjMJwMA"
    : "https://docs.google.com/spreadsheets/d/1-aR9iT6bCl_hS1x2PsHjpjsJ540U0_1MchrPD-K76xQ";

  public billingInvoiceSpreadsheetUrl = environment.production
    ? "https://docs.google.com/spreadsheets/d/1Uo2TlX0e40PAr4YkM8RkJGaPzg-LegjdM2-DvW7SdxM"
    : "https://docs.google.com/spreadsheets/d/1jjUuPJVqimvSxR1oi9w4Rj-i50Fc_Buhv9MkDpNML3g";

  public form = new FormGroup<AddClientForm>({
    domain: new FormControl("", [
      (c) => Validators.required(c),
      validateHTTPExists,
    ]),
    googleAdminEmail: new FormControl("", (c) => Validators.required(c)),
    planId: new FormControl<ClientPlanId>("free", {
      nonNullable: true,
      validators: [(c) => Validators.required(c)],
    }),
    isIncludedDemoContent: new FormControl(false),
    trialFinishDate: new FormControl<Date | null>(defaultTrialFinishDate, [
      (c) => Validators.required(c),
      validateFutureDate,
    ]),
  });

  public reloadClients = new ReplaySubject<number>(1);
  public clients$: Observable<ClientWithStats[]>;

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
    private service: ClientService,
    private onboardingService: OnboardingService,
    private toastrService: ToastrService,
    private reportsService: ReportsService,
    private router: Router,
    private http: HttpClient,
    public auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.clients$ = this.reloadClients.pipe(
      switchMap(() => this.service.getAll()),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.clientsSubscription = this.clients$.subscribe((clients) => {
      this.currentClients = clients;
    });

    this.form
      .get("planId")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((planId) => {
        const trialControl = this.form.get("trialFinishDate");
        if (!trialControl) {
          return;
        }
        if (planId === "free") {
          trialControl.setValidators(this.trialFinishDateValidations);
          if (!trialControl.value) {
            trialControl.setValue(defaultTrialFinishDate);
          }
        } else {
          trialControl.clearValidators();
          trialControl.setValue(null);
        }
        trialControl.updateValueAndValidity();
      });

    this.loadData();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.clientsSubscription) {
      this.clientsSubscription.unsubscribe();
    }
  }

  loadData = () => {
    this.reloadClients.next(1);
  };

  add = async () => {
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.valid) {
      this.saving = true;
      const formData = this.form.getRawValue();

      try {
        await this.executeFetchStep(formData);

        this.loadData();

        this.toastrService.success(
          this.translate.instant("client.addSuccess") as string,
        );
      } catch (error: unknown) {
        this.toastrService.error(this.getAddClientErrorMessage(error));
      } finally {
        this.saving = false;
      }
    }
  };

  private async executeFetchStep(formData: {
    domain: string;
    googleAdminEmail: string;
    isIncludedDemoContent: boolean;
    trialFinishDate: Date | null;
    planId: ClientPlanId;
  }): Promise<void> {
    const planId = formData.planId;
    await this.onboardingService
      .createDomainOnboarding(formData.domain, {
        domain: formData.domain,
        googleAdminEmail: formData.googleAdminEmail,
        isIncludedDemoContent: formData.isIncludedDemoContent,
        trialFinishDate:
          planId === "free"
            ? (formData.trialFinishDate ?? undefined)
            : undefined,
        planDetails: {
          planId,
          planName: CLIENT_PLAN_NAMES[planId],
        },
      })
      .toPromise();

    await this.onboardingService
      .enrichWithBrandData(formData.domain)
      .toPromise();

    await this.service
      .sendClientOnboardingLink(
        formData.googleAdminEmail,
        formData.domain,
        "en",
      )
      .toPromise();
  }

  private getAddClientErrorMessage(error: unknown): string {
    const backendMessage =
      (error as { error?: { message?: string } })?.error?.message || "";

    if (backendMessage === "DOMAIN_ALREADY_EXISTS") {
      return this.translate.instant("client.domainAlreadyExists") as string;
    }

    return this.translate.instant("client.addError") as string;
  }

  remove = (client: ClientWithStats) => {
    if (!this.canBeDeleted(client)) {
      if (client.planId !== "free" && client.planId !== "freemium") {
        this.toastrService.error(
          this.translate.instant("client.cannotDeletePaidPlan") as string,
        );
      } else if (
        !client.parentDomain &&
        this.currentClients.some((c) => c.parentDomain === client.domain)
      ) {
        this.toastrService.error(
          this.translate.instant("client.cannotDeleteWithSubdomains") as string,
        );
      }
      return;
    }

    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: this.translate.instant("client.deleteConfirmationText", {
            name: client.domain,
          }) as string,
        },
      })
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.removing = true;
          this.removingDomain = client.domain;
          this.service
            .remove(client.clientId)
            .toPromise()
            .then(() => {
              this.loadData();
              this.toastrService.success(
                this.translate.instant("client.deleteSuccess") as string,
              );
            })
            .catch((error: unknown) => {
              this.toastrService.error(
                (error as { error?: { message?: string } })?.error?.message ||
                  (this.translate.instant("client.deleteError") as string),
              );
            })
            .finally(() => {
              this.removing = false;
              this.removingDomain = null;
            });
        }
      });
  };

  canBeDeleted = (c: ClientWithStats) => {
    if (c.planId !== "free") {
      return false;
    }

    if (!c.parentDomain) {
      const hasSubdomains = this.currentClients.some(
        (client) => client.parentDomain === c.domain,
      );
      if (hasSubdomains) {
        return false;
      }
    }

    return true;
  };

  getDeleteTooltip = (client: ClientWithStats) => {
    if (!this.canBeDeleted(client)) {
      if (client.planId !== "free" && client.planId !== "freemium") {
        return this.translate.instant("client.cannotDeletePaidPlan") as string;
      }
      if (
        !client.parentDomain &&
        this.currentClients.some((c) => c.parentDomain === client.domain)
      ) {
        return this.translate.instant(
          "client.cannotDeleteWithSubdomains",
        ) as string;
      }
    }

    return client.parentDomain
      ? (this.translate.instant("client.deleteSubdomainTooltip") as string)
      : (this.translate.instant("client.deleteTooltip") as string);
  };

  edit(c: ClientWithStats) {
    void this.router.navigate(["/admin/client", c.clientId]);
  }

  generateReport(): void {
    this.clients$.subscribe((clients) => {
      this.reportsService.generateClientsReport(clients);
    });
  }

  downloadAnalyticsReport(): void {
    const dialogRef = this.dialog.open<
      AnalyticsRangeDialogComponent,
      unknown,
      AnalyticsRangeDialogResult
    >(AnalyticsRangeDialogComponent, {
      width: "520px",
      maxWidth: "95vw",
      panelClass: "app-dialog-panel",
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      this.isDownloadingAnalytics = true;
      const params: Record<string, string> = {
        from: result.from.toISOString(),
      };
      if (result.to) {
        params["to"] = result.to.toISOString();
      }

      this.http
        .get("/analytics/csv", {
          responseType: "blob",
          observe: "response",
          params,
        })
        .subscribe({
          next: (response) => {
            if (!response.body) {
              this.toastrService.error(
                this.translate.instant("client.analyticsReportError") as string,
              );
              this.isDownloadingAnalytics = false;
              return;
            }
            const blob = new Blob([response.body], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `analytics-report.csv`;
            link.click();
            window.URL.revokeObjectURL(url);
            this.toastrService.success(
              this.translate.instant(
                "client.analyticsReportDownloaded",
              ) as string,
            );
          },
          error: () => {
            this.toastrService.error(
              this.translate.instant("client.analyticsReportError") as string,
            );
          },
          complete: () => {
            this.isDownloadingAnalytics = false;
          },
        });
    });
  }

  runExpiredTrialCron(): void {
    this.service.runExpiredTrialCron().subscribe({
      next: () => {
        this.toastrService.success(
          this.translate.instant("client.expiredTrialCronSuccess") as string,
        );
        this.loadData();
      },
      error: () => {
        this.toastrService.error(
          this.translate.instant("client.expiredTrialCronError") as string,
        );
      },
    });
  }

  openSuperAdminDialog(): void {
    this.dialog.open(SuperAdminDialogComponent, {
      width: "500px",
      panelClass: "app-dialog-panel",
    });
  }
}
